// Drawers de la tab {Zonas} de Administración · {Tiendas y zonas}
// (handoff README §4): «Cambiar responsable» y «Renombrar» sobre
// PUT /asset-groups/:id, «Nueva {zona}» sobre POST /asset-groups.
//
// El responsable viaja como `supervisorUserId` + `replaceSupervisor: true`:
// la ruta exige el flag para desplazar a un titular vivo (ADR-0124 D10,
// 409 ASSET_GROUP_SUPERVISOR_OCCUPIED sin él) y lo ignora cuando la zona
// no tenía a nadie, así que el drawer lo manda siempre — ese es
// exactamente el traspaso que el copy describe («el anterior deja de
// verlas»).
//
// No se muestra el checkbox «Avisar por correo al nuevo responsable» del
// handoff: ni PUT /asset-groups/:id ni ninguna ruta tiene ese flag.
//
// El id del responsable actual no cruza el wire (GET /asset-groups trae
// sólo `subline`); la pantalla lo resuelve cruzando el nombre con el
// roster y lo pasa ya resuelto (`currentLeadUserId`), o null con
// `hasLead` cuando la zona tiene responsable pero el servidor no supo su
// nombre. En ese caso el select parte vacío sin negar al titular.

import { useState } from "react";
import type { AssetGroupCreateBody, AssetGroupUpdateBody, OrgMember } from "@bitacorapro/api-types";
import { Drawer } from "../../../components/Drawer";
import type { AdminAssetGroupRow } from "./api";
import { Field, saveErrorMessage } from "./form";
import { useCreateAdminAssetGroup, useUpdateAdminAssetGroup } from "./queries";
import type { ActivosWords } from "./words";

/** Los miembros activos cuyo rol tiene alcance de zona. */
export function groupLeadCandidates(members: readonly OrgMember[], w: ActivosWords): OrgMember[] {
  return members
    .filter((m) => w.groupLeadRoleKeys.includes(m.role) && !m.disabledAt)
    .sort((a, b) => a.displayName.localeCompare(b.displayName, "es"));
}

function assetCount(g: AdminAssetGroupRow): number {
  return g.stats[0]?.n ?? 0;
}

// Mismo texto en los dos drawers cuando nadie del roster tiene el rol de
// la zona; el select queda con su única opción vacía y Guardar/Crear no
// dependen de él.
function noCandidatesHint(w: ActivosWords): string {
  return `Nadie tiene el rol ${w.groupLead} todavía. Invita a una persona con ese rol desde Equipo y accesos.`;
}

function LeadSelect({
  ctl,
  value,
  onChange,
  candidates,
  emptyLabel,
}: {
  ctl: { id: string; "aria-describedby": string | undefined; "aria-invalid": true | undefined };
  value: string;
  onChange: (next: string) => void;
  candidates: readonly OrgMember[];
  emptyLabel: string;
}) {
  return (
    <select {...ctl} className="select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{emptyLabel}</option>
      {candidates.map((m) => (
        <option key={m.userId} value={m.userId}>
          {m.displayName}
        </option>
      ))}
    </select>
  );
}

// ── Cambiar responsable ──────────────────────────────────────────────

export type GroupLeadDrawerProps = {
  group: AdminAssetGroupRow;
  /** Id del responsable actual resuelto contra el roster; null si no se pudo. */
  currentLeadUserId: string | null;
  /** La zona tiene responsable (hay `subline`), se haya resuelto o no. */
  hasLead: boolean;
  members: readonly OrgMember[];
  words: ActivosWords;
  onClose: () => void;
  onSaved: (message: string) => void;
};

export function GroupLeadDrawer({
  group,
  currentLeadUserId,
  hasLead,
  members,
  words: w,
  onClose,
  onSaved,
}: GroupLeadDrawerProps) {
  const candidates = groupLeadCandidates(members, w);
  // Preselecciona al titular sólo si sigue siendo candidato (rol de zona,
  // no deshabilitado); si no, el select parte vacío.
  const current = candidates.some((m) => m.userId === currentLeadUserId)
    ? (currentLeadUserId as string)
    : "";
  const [lead, setLead] = useState(current);
  const [formError, setFormError] = useState<string | null>(null);
  const update = useUpdateAdminAssetGroup();
  const n = assetCount(group);
  const dirty = lead !== "" && lead !== current;

  async function submit() {
    setFormError(null);
    if (!dirty) return;
    // El id viene del <select> como string; el wire lo marca (brand).
    const body: AssetGroupUpdateBody = {
      supervisorUserId: lead as NonNullable<AssetGroupUpdateBody["supervisorUserId"]>,
      replaceSupervisor: true,
    };
    try {
      await update.mutateAsync({ id: group.id, body });
      onSaved(`Responsable actualizado · ve sus ${n} ${w.assetManyLower} desde ahora`);
    } catch (err) {
      setFormError(saveErrorMessage(err, w));
    }
  }

  return (
    <Drawer
      title={`Cambiar responsable · ${group.name}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin cambiar el responsable"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={`Dejar a esta persona como responsable de la ${w.groupOneLower}`}
            disabled={!dirty || update.isPending}
            onClick={() => void submit()}
          >
            {update.isPending ? "Guardando…" : "Guardar"}
          </button>
        </>
      }
    >
      <p className="ag-muted ag-mb16">
        El responsable ve todas las {w.assetManyLower} de la {w.groupOneLower} y recibe sus avisos.
        El anterior deja de verlas, salvo que tenga otro acceso.
      </p>
      <Field label="Responsable" hint={candidates.length === 0 ? noCandidatesHint(w) : undefined}>
        {(ctl) => (
          <LeadSelect
            ctl={ctl}
            value={lead}
            onChange={setLead}
            candidates={candidates}
            emptyLabel={
              current
                ? "Elige a otra persona"
                : hasLead
                  ? "Elige a la nueva persona"
                  : "Elige a una persona"
            }
          />
        )}
      </Field>
      {formError && (
        <div className="auth-error" role="alert">
          {formError}
        </div>
      )}
    </Drawer>
  );
}

// ── Renombrar ────────────────────────────────────────────────────────

export type GroupRenameDrawerProps = {
  group: AdminAssetGroupRow;
  words: ActivosWords;
  onClose: () => void;
  onSaved: (message: string) => void;
};

export function GroupRenameDrawer({ group, words: w, onClose, onSaved }: GroupRenameDrawerProps) {
  const [name, setName] = useState(group.name);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const update = useUpdateAdminAssetGroup();
  const n = assetCount(group);
  const trimmed = name.trim();
  const dirty = trimmed !== group.name;

  async function submit() {
    setFormError(null);
    if (!trimmed) {
      setError(`Escribe el nombre de la ${w.groupOneLower}.`);
      return;
    }
    setError(null);
    try {
      await update.mutateAsync({ id: group.id, body: { name: trimmed } });
      onSaved("Nombre actualizado");
    } catch (err) {
      setFormError(saveErrorMessage(err, w));
    }
  }

  return (
    <Drawer
      title={`Renombrar · ${group.name}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin renombrar"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={`Guardar el nuevo nombre de la ${w.groupOneLower}`}
            disabled={!dirty || update.isPending}
            onClick={() => void submit()}
          >
            {update.isPending ? "Guardando…" : "Guardar"}
          </button>
        </>
      }
    >
      <Field
        label="Nombre"
        hint={`Las ${n} ${w.assetManyLower} siguen en esta ${w.groupOneLower}.`}
        error={error}
      >
        {(ctl) => (
          <input
            {...ctl}
            className="input"
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
          />
        )}
      </Field>
      {formError && (
        <div className="auth-error" role="alert">
          {formError}
        </div>
      )}
    </Drawer>
  );
}

// ── Nueva {zona} ─────────────────────────────────────────────────────

export type NewGroupDrawerProps = {
  members: readonly OrgMember[];
  words: ActivosWords;
  onClose: () => void;
  onSaved: (message: string) => void;
};

export function NewGroupDrawer({ members, words: w, onClose, onSaved }: NewGroupDrawerProps) {
  const candidates = groupLeadCandidates(members, w);
  const [name, setName] = useState("");
  const [lead, setLead] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const create = useCreateAdminAssetGroup();
  const trimmed = name.trim();
  const dirty = trimmed !== "" || lead !== "";

  async function submit() {
    setFormError(null);
    if (!trimmed) {
      setError(`Escribe el nombre de la ${w.groupOneLower}.`);
      return;
    }
    setError(null);
    const body: AssetGroupCreateBody = {
      name: trimmed,
      // El `kind` que la receta declara para sus grupos; la ruta lo valida
      // cuando viaja y no exige nada cuando no.
      ...(w.groupKind ? { kind: w.groupKind } : {}),
      ...(lead ? { supervisorUserId: lead } : {}),
    } as AssetGroupCreateBody;
    try {
      await create.mutateAsync(body);
      onSaved("Guardado");
    } catch (err) {
      setFormError(saveErrorMessage(err, w));
    }
  }

  return (
    <Drawer
      title={w.groupNew}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin crear"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={`Crear la ${w.groupOneLower} con estos datos`}
            disabled={!dirty || create.isPending}
            onClick={() => void submit()}
          >
            {create.isPending ? "Creando…" : "Crear"}
          </button>
        </>
      }
    >
      <Field label="Nombre" error={error}>
        {(ctl) => (
          <input
            {...ctl}
            className="input"
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
          />
        )}
      </Field>
      <Field label="Responsable" hint={candidates.length === 0 ? noCandidatesHint(w) : undefined}>
        {(ctl) => (
          <LeadSelect
            ctl={ctl}
            value={lead}
            onChange={setLead}
            candidates={candidates}
            emptyLabel="Sin asignar"
          />
        )}
      </Field>
      {formError && (
        <div className="auth-error" role="alert">
          {formError}
        </div>
      )}
    </Drawer>
  );
}
